import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsPortalApi } from '@/api/projectsPortal.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { PortalInvoice, ProjectTask } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectClientPortalLoginPage() {
  const [organizationId, setOrganizationId] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const requestLink = async () => {
    setLoading(true);
    try {
      await projectsPortalApi.requestLink(Number(organizationId), email);
      notify.success('If registered, a login link was sent to your email');
    } catch (err) {
      showApiError(err, 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md pt-12">
      <PageHeader title="Project client portal" description="View your project status, tasks, documents, and invoices" />
      <div className="card space-y-3 p-6">
        <Input label="Organization ID" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} />
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button loading={loading} onClick={() => void requestLink()}>Send magic link</Button>
      </div>
    </div>
  );
}

export function ProjectClientPortalVerifyPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => new URLSearchParams(window.location.search).get('token') ?? '');
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    setLoading(true);
    try {
      const res = await projectsPortalApi.verify(token);
      projectsPortalApi.setToken(res.accessToken);
      notify.success('Signed in');
      navigate('/projects-portal/dashboard');
    } catch (err) {
      showApiError(err, 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md pt-12">
      <PageHeader title="Verify login" description="Complete client portal sign-in" />
      <div className="card space-y-3 p-6">
        <Input label="Token" value={token} onChange={(e) => setToken(e.target.value)} />
        <Button loading={loading} onClick={() => void verify()}>Verify</Button>
      </div>
    </div>
  );
}

export function ProjectClientPortalDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'tasks' | 'documents' | 'invoices'>('tasks');
  const [dashboard, setDashboard] = useState<Awaited<ReturnType<typeof projectsPortalApi.dashboard>> | null>(null);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [documents, setDocuments] = useState<Awaited<ReturnType<typeof projectsPortalApi.documents>>>([]);
  const [invoices, setInvoices] = useState<PortalInvoice[]>([]);

  useEffect(() => {
    if (!projectsPortalApi.getToken()) {
      navigate('/projects-portal/login');
      return;
    }
    projectsPortalApi.dashboard().then(setDashboard).catch(() => navigate('/projects-portal/login'));
    projectsPortalApi.tasks().then(setTasks).catch(() => undefined);
    projectsPortalApi.documents().then(setDocuments).catch(() => undefined);
    projectsPortalApi.invoices().then(setInvoices).catch(() => undefined);
  }, [navigate]);

  return (
    <div className="mx-auto max-w-3xl pt-8 px-4">
      <PageHeader title={dashboard?.projectName ?? 'Project portal'} description={`Status: ${dashboard?.lifecycleStatus ?? '—'}`} />
      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <div className="card p-4"><p className="text-sm text-slate-500">Tasks</p><p className="text-2xl font-semibold">{dashboard?.openTasks ?? '—'}</p></div>
        <div className="card p-4"><p className="text-sm text-slate-500">Issues</p><p className="text-2xl font-semibold">{dashboard?.openIssues ?? '—'}</p></div>
        <div className="card p-4"><p className="text-sm text-slate-500">Milestones</p><p className="text-2xl font-semibold">{dashboard?.milestones ?? '—'}</p></div>
      </div>
      <div className="flex gap-2 mb-4">
        {(['tasks', 'documents', 'invoices'] as const).map((t) => (
          <Button key={t} variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(t)}>{t}</Button>
        ))}
      </div>
      <div className="card p-4">
        {tab === 'tasks' && <ul className="space-y-2">{tasks.map((t) => <li key={t.id} className="text-sm border-b pb-2">{t.title}</li>)}</ul>}
        {tab === 'documents' && (
          <ul className="space-y-2">
            {documents.map((d) => (
              <li key={d.id} className="text-sm border-b pb-2">
                <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">{d.fileName}</a>
              </li>
            ))}
          </ul>
        )}
        {tab === 'invoices' && (
          <ul className="space-y-2">
            {invoices.map((inv) => (
              <li key={inv.id} className="text-sm border-b pb-2 flex justify-between">
                <span>{inv.invoiceNumber}</span>
                <span>{inv.totalAmount} {inv.currencyCode} — {inv.statusCode}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
